import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ScrollToTop from './utils/ScrollToTop';
import { HelmetProvider } from "react-helmet-async";
import {
  bindConsentTrackingUpdates,
  initGTM,
  trackPageView,
} from "./utils/tracking";

// Components
import PublicPageLayout from "./rendering/PublicPageLayout";
import { publicPrerenderRoutes } from "./rendering/publicRoutes";

// Pages
import Resources from './pages/Resources';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Success from './pages/Success';
import Cancel from './pages/Cancel';
import Blogs from './pages/Blogs.jsx';
import SingleBlog from './pages/blogs/SingleBlog';
// Field Event is paused; keep the page component for possible future reuse.
// import FieldEventCrossfitHaarlem from "./pages/FieldEventCrossfitHaarlem.jsx";

// Auth
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import PaidRoute from "./components/PaidRoute";
import AdminRoute from "./components/AdminRoute";
import DashboardLayout from "./layouts/DashboardLayout";

// Dashboard Pages
const Dashboard = lazy(() => import("./dashboard/Dashboard"));
const Library = lazy(() => import("./dashboard/library/Library"));
const TrainingSchedule = lazy(() => import("./dashboard/trainingschedule/TrainingSchedule.jsx"));
const Progress = lazy(() => import("./dashboard/progress/Progress"));
const CheckIn = lazy(() => import("./dashboard/checkin/CheckIn"));
const Messages = lazy(() => import("./dashboard/messages/Messages"));
const Nutrition = lazy(() => import("./dashboard/nutrition/Nutrition"));
const Settings = lazy(() => import("./dashboard/settings/Settings"));

// Admin Pages
const AdminHome = lazy(() => import('./admin/AdminHome.jsx'));
const AdminDashboard = lazy(() => import("./admin/AdminDashboard"));
const UploadVideo = lazy(() => import("./admin/UploadVideo"));
const ManageVideos = lazy(() => import("./admin/ManageVideos"));
const ManageUsers = lazy(() => import("./admin/ManageUsers"));
const ManageTrainingProgram = lazy(() => import('./admin/ManageTrainingProgram'));
const UploadTrainingProgram = lazy(() => import('./admin/UploadTrainingProgram'));
const MessagesAdmin = lazy(() => import('./admin/MessagesAdmin.jsx'));
const UploadBlog = lazy(() => import('./admin/UploadBlog.jsx'));
const ManageBlog = lazy(() => import('./admin/ManageBlog.jsx'));
const ManageSocialPosts = lazy(() => import('./admin/ManageSocialPosts.jsx'));

// Layout
const Stopwatch = lazy(() => import('./dashboard/stopwatch/StopWatch'));

const TrackingProvider = () => {
  const location = useLocation();

  useEffect(() => {
    initGTM();
    return bindConsentTrackingUpdates();
  }, []);

  useEffect(() => {
    window.setTimeout(() => {
      trackPageView(location.pathname);
    }, 0);
  }, [location.pathname, location.search]);

  return null;
};

const MainRoutes = () => {
  const location = useLocation();
  const isDashboardRoute = location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/library") ||
    location.pathname.startsWith("/trainingprogram") ||
    location.pathname.startsWith("/check-in") ||
    location.pathname.startsWith("/trainingschedule") ||
    location.pathname.startsWith("/progress") ||
    location.pathname.startsWith("/stopwatch") ||
    location.pathname.startsWith("/nutrition") ||
    location.pathname.startsWith("/messages") ||
    location.pathname.startsWith("/settings") ||
    location.pathname.startsWith("/admin");

  return (
<>
  <TrackingProvider />
  {isDashboardRoute && <ScrollToTop />}

  {isDashboardRoute ? (
    <Suspense fallback={null}>
      <Routes>
        {/* Protected Dashboard Routes with Shared Sidebar */}
        <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>

          {/* Free Access (all users) */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/stopwatch" element={<Stopwatch />} />
          <Route path="/settings" element={<Settings />} />

          {/* Basic+ (basic, intermediate, full, admin) */}
          <Route
            path="/trainingprogram"
            element={<Navigate to="/dashboard" replace />}
          />

          {/* Intermediate+ (intermediate, full, admin) */}
          <Route
            path="/library"
            element={
              <PaidRoute requiredLevels={["intermediate", "full", "admin"]}>
                <Library />
              </PaidRoute>
            }
          />
          <Route
            path="/trainingschedule"
            element={
              <PaidRoute requiredLevels={["intermediate", "full", "admin"]}>
                <TrainingSchedule />
              </PaidRoute>
            }
          />
          <Route
            path="/progress"
            element={
              <PaidRoute requiredLevels={["intermediate", "full", "admin"]}>
                <Progress />
              </PaidRoute>
            }
          />
          <Route
            path="/nutrition"
            element={
              <PaidRoute requiredLevels={["intermediate", "full", "admin"]}>
                <Nutrition />
              </PaidRoute>
            }
          />
          <Route
            path="/check-in"
            element={
              <PaidRoute requiredLevels={["intermediate", "full", "admin"]}>
                <CheckIn />
              </PaidRoute>
            }
          />

          {/* Full Only (full, admin) */}
          <Route
            path="/messages"
            element={
              <PaidRoute requiredLevels={["full", "admin"]}>
                <Messages />
              </PaidRoute>
            }
          />
        </Route>


        {/* Admin Dashboard Routes */}
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>}>
          <Route index element={<AdminHome />} />
          <Route path="upload" element={<UploadVideo />} />
          <Route path="manage-videos" element={<ManageVideos />} />
          <Route path="manage-users" element={<ManageUsers />} />
          <Route path="manage-program" element={<ManageTrainingProgram />} />
          <Route path="upload-program" element={<UploadTrainingProgram />} />
          <Route path="manage-blog" element={<ManageBlog />} />
          <Route path="upload-blog" element={<UploadBlog />} />
          <Route path="social-posts" element={<ManageSocialPosts />} />
          <Route path="messagesadmin" element={<MessagesAdmin />} />
        </Route>
      </Routes>
    </Suspense>
  ) : (
    <PublicPageLayout>
      <Routes location={location} key={location.pathname}>
        {/* Public Pages */}
        {publicPrerenderRoutes.map((route) => <Route key={route.path} {...route} />)}
        {/* Field Event is paused; redirect direct visits until the event resumes. */}
        <Route path="/field-event-crossfit-haarlem" element={<Navigate to="/" replace />} />
        {/* <Route path="/field-event-crossfit-haarlem" element={<FieldEventCrossfitHaarlem />} /> */}
        <Route path="/resources" element={<Resources />} />
        <Route path="/blogs" element={<Blogs />} />
        <Route path="/blogs/:slug" element={<SingleBlog />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/success" element={<Success />} />
        <Route path="/cancel" element={<Cancel />} />
        <Route path="/aanbod" element={<Navigate to="/pricing" replace />} />
      </Routes>
    </PublicPageLayout>
  )}

</>

  );
};

const App = () => {
  return (
    <AuthProvider>
      <HelmetProvider>
        <Router>
          <MainRoutes />
        </Router>
      </HelmetProvider>
    </AuthProvider>
  );
};


export default App;
